/**
 * InstitutionTypeFilter — multi-select dropdown for restricting the
 * `/advanced` result set by `institution_type`. Values persist in the
 * URL as a comma-separated `?inst=` list, so a family can share
 * "FE colleges + conservatoires in Bristol" as a single link.
 *
 * Consumes the same URL param the single-value deep-link uses
 * (the FE landing sends `?inst=further_education_college`), so no
 * migration is needed — a single value is just an array of length 1.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

// `university` is the default pool (also covers legacy editorial rows
// with no `institution_type` field — see backend/routes/profile.py).
// Listed first so the family journey "just universities" is a single
// click away, and picking it can restore the original result set
// after they've narrowed to a niche type.
const OPTIONS = [
  { value: "university", label: "Universities" },
  { value: "further_education_college", label: "FE colleges" },
  { value: "university_centre", label: "University centres" },
  { value: "conservatoire", label: "Conservatoires" },
  { value: "specialist_he_provider", label: "Specialist HE providers" },
  { value: "specialist_he_college", label: "Specialist HE colleges" },
  { value: "private_he_provider", label: "Private HE providers" },
];

export default function InstitutionTypeFilter({ value, onChange }) {
  const selected = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click. Kept minimal — no portal / positioning
  // helper because the filter row is always in the top viewport
  // strip so overflow / clipping isn't a concern.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (v) => {
    if (selected.includes(v)) {
      onChange(selected.filter((x) => x !== v).join(","));
    } else {
      onChange([...selected, v].join(","));
    }
  };
  const clear = () => onChange("");

  const label =
    selected.length === 0
      ? "Institution type"
      : selected.length === 1
      ? OPTIONS.find((o) => o.value === selected[0])?.label || selected[0]
      : `${selected.length} institution types`;

  const active = selected.length > 0;

  return (
    <div ref={ref} className="relative inline-block" data-testid="institution-type-filter">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="institution-type-filter-toggle"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-underground-sc tracking-wider transition-colors " +
          (active
            ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3a1476]"
            : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
        }
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-80" />
      </button>

      {open && (
        <div
          data-testid="institution-type-filter-menu"
          className="absolute z-30 mt-2 min-w-[220px] rounded-md border border-stone-200 bg-white shadow-lg py-1 text-sm"
          role="listbox"
        >
          {OPTIONS.map((o) => {
            const on = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(o.value)}
                data-testid={`institution-type-filter-option-${o.value}`}
                className={
                  "w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-stone-50 " +
                  (on ? "text-[#4C1D95] font-medium" : "text-stone-700")
                }
              >
                <span
                  className={
                    "h-4 w-4 rounded border flex items-center justify-center shrink-0 " +
                    (on
                      ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                      : "border-stone-300 bg-white text-transparent")
                  }
                  aria-hidden
                >
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
                {o.label}
              </button>
            );
          })}
          {active && (
            <>
              <div className="my-1 border-t border-stone-100" />
              <button
                type="button"
                onClick={() => { clear(); setOpen(false); }}
                data-testid="institution-type-filter-clear"
                className="w-full text-left px-3 py-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-rose-700 hover:bg-rose-50/40"
              >
                Clear all
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
